import { ImageUp, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { subirArchivo, validarImagen, type DestinoSubida } from '@/lib/archivos';

/**
 * Imagen que se sube al CDN en cuanto se elige (`POST /admin/uploads`). El valor es el nombre del
 * archivo (cupones y becas) o la ruta (`modo="ruta"`), y se guarda con el formulario que la usa.
 */
export function SubirImagen({
  destino,
  ownerId,
  valor,
  urlDe,
  onCambio,
  modo = 'nombre',
  deshabilitado = false,
  etiqueta = 'Imagen',
}: {
  destino: DestinoSubida;
  ownerId?: number;
  valor: string | null | undefined;
  urlDe: (valor: string) => string;
  onCambio: (valor: string | null) => void;
  modo?: 'nombre' | 'ruta';
  deshabilitado?: boolean;
  etiqueta?: string;
}) {
  const [subiendo, setSubiendo] = useState(false);
  const [vista, setVista] = useState<string | null>(null);

  async function elegir(file: File | undefined) {
    if (!file) return;
    const error = validarImagen(file);
    if (error) {
      toast.error(error);
      return;
    }
    setSubiendo(true);
    try {
      const r = await subirArchivo(file, destino, ownerId);
      setVista(r.url);
      onCambio(modo === 'ruta' ? r.path : r.fileName);
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setSubiendo(false);
    }
  }

  const src = valor ? (vista ?? urlDe(valor)) : null;
  return (
    <div>
      <span className="mb-1.5 block text-sm font-bold text-texto-2">{etiqueta}</span>
      <div className="flex items-center gap-3">
        {src ? (
          <img src={src} alt={etiqueta} className="h-20 max-w-48 rounded-lg object-contain" />
        ) : (
          <span className="grid h-20 w-32 place-items-center rounded-lg border border-dashed border-[var(--separador)] text-xs text-texto-3">
            Sin imagen
          </span>
        )}
        {!deshabilitado && (
          <div className="flex flex-col gap-2">
            <label className="btn-cs btn-vidrio cursor-pointer">
              <ImageUp size={16} aria-hidden /> {subiendo ? 'Subiendo…' : 'Elegir imagen'}
              <input
                type="file"
                className="sr-only"
                accept="image/jpeg,image/png,image/webp,image/gif"
                disabled={subiendo}
                onChange={(e) => {
                  void elegir(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {valor && (
              <button
                type="button"
                className="btn-cs btn-vidrio"
                onClick={() => {
                  setVista(null);
                  onCambio(null);
                }}
              >
                <Trash2 size={16} aria-hidden /> Quitar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
